import { useEffect, useCallback } from 'react';
import { debugLog } from '../Stores/DebugLogStore';

const POLL_INTERVAL_MS = 60_000; // Check every 60 seconds

// Once we detect a newer deploy we reload to pick it up. If a reload does NOT
// actually load the newer build (e.g. an intermediary cache keeps serving a
// stale index.html despite `no-cache`), we must not reload again for that same
// server version — otherwise a kiosk would reload forever. sessionStorage
// survives a reload but is cleared on a full app restart, so a cold relaunch
// gets one fresh attempt.
const RELOAD_GUARD_KEY = 'appVersionReloadedFor';

/**
 * Auto-updates an installed/open PWA. Polls version.json and, when the deployed
 * build differs from the one this bundle was built as (__APP_BUILD_ID__),
 * reloads to fetch the new hashed assets. Because it compares against the
 * compiled-in build id (not just "whatever the server said first"), it also
 * self-heals a stale initial load, not only deploys that happen while running.
 */
export function useAppVersionCheck() {
    const checkVersion = useCallback(async () => {
        try {
            const resp = await fetch(`./version.json?_t=${Date.now()}`, {
                cache: 'no-store',
            });
            if (!resp.ok) return;

            const data = (await resp.json()) as { version: string };
            const serverVersion = data.version;

            // The running bundle is the deployed build — nothing to do.
            if (serverVersion === __APP_BUILD_ID__) return;

            // A different build is deployed. Reload once per server version per
            // session so a stale-serving cache can't trap us in a reload loop.
            if (sessionStorage.getItem(RELOAD_GUARD_KEY) === serverVersion) {
                debugLog.info(
                    `Build ${serverVersion} deployed but a reload was already attempted this session ` +
                        `(running ${__APP_BUILD_ID__}); skipping to avoid a loop.`
                );
                return;
            }

            debugLog.info(`New build detected (${__APP_BUILD_ID__} → ${serverVersion}). Reloading…`);
            sessionStorage.setItem(RELOAD_GUARD_KEY, serverVersion);
            setTimeout(() => window.location.reload(), 500);
        } catch {
            // Network error or storage unavailable — skip silently.
        }
    }, []);

    useEffect(() => {
        // In dev the injected build id never matches the static public/version.json,
        // and HMR already handles updates — so don't poll or reload.
        if (import.meta.env.DEV) {
            debugLog.info(`App build ${__APP_BUILD_ID__} (dev — auto-update disabled)`);
            return;
        }

        checkVersion();
        const interval = setInterval(checkVersion, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [checkVersion]);
}
